import * as THREE from 'three';

/**
 * Civil categories surfaced to the UI. Used for color-by-category, the
 * properties drawer, and quantity reports.
 */
export type LandXMLCategory =
  | 'SURFACE'
  | 'ALIGNMENT'
  | 'PARCEL'
  | 'BREAKLINE'
  | 'PIPE'
  | 'STRUCTURE';

/**
 * Per-feature metadata. Only the fields relevant to the feature's category
 * are populated — everything else is undefined.
 */
export interface LandXMLFeatureMetadata {
  // Surface
  surfaceArea?: number;       // sum of TIN triangle areas (m²)
  triangleCount?: number;
  vertexCount?: number;
  zMin?: number;
  zMax?: number;
  // Alignment / breakline / pipe centerline
  length?: number;            // 3D polyline length (m)
  polyline?: THREE.Vector3[]; // raw polyline (alignments — used for stationing)
  cumLengths?: number[];      // arc-length per polyline vertex (alignments)
  // Parcel
  parcelArea?: number;        // planar XY area via shoelace (m²)
  // Pipe
  diameter?: number;          // outer diameter (m)
  pipeShape?: PipeShape;
  invertStart?: number;       // invert elevation at start (m)
  invertEnd?: number;         // invert elevation at end (m)
  slope?: number;             // (invertEnd-invertStart)/horizLength
  // Structure
  rimElev?: number;
  sumpElev?: number;
  innerDiameter?: number;
}

export interface LandXMLFeature {
  geometry: THREE.BufferGeometry;
  name: string;
  category: LandXMLCategory;
  metadata: LandXMLFeatureMetadata;
}

export interface PipeNetworkResult {
  /** Pipe + structure meshes (3D solids). */
  meshes: LandXMLFeature[];
  /** Pipe centerlines (lines). */
  lines: LandXMLFeature[];
}

// --- geometry math helpers ----------------------------------------------

function polylineLength(points: ReadonlyArray<THREE.Vector3>): number {
  let len = 0;
  for (let i = 1; i < points.length; i++) len += points[i].distanceTo(points[i - 1]);
  return len;
}

function cumulativePolylineLengths(points: ReadonlyArray<THREE.Vector3>): number[] {
  const out: number[] = new Array(points.length);
  out[0] = 0;
  for (let i = 1; i < points.length; i++) out[i] = out[i - 1] + points[i].distanceTo(points[i - 1]);
  return out;
}

/** Shoelace area of a closed XY polygon (last point may be a duplicate of first). */
function polygonAreaXY(points: ReadonlyArray<THREE.Vector3>): number {
  if (points.length < 3) return 0;
  let s = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    s += a.x * b.y - b.x * a.y;
  }
  return Math.abs(s) * 0.5;
}

/**
 * Project a 3D point onto an alignment polyline (segment by segment) and
 * return the resulting station (arc-length from start) plus the
 * perpendicular offset to the nearest segment. `null` when the alignment
 * doesn't carry polyline + cumLengths metadata.
 */
export function projectPointOntoAlignment(
  point: THREE.Vector3,
  polyline: ReadonlyArray<THREE.Vector3>,
  cumLengths: ReadonlyArray<number>,
): { station: number; offset: number } | null {
  if (polyline.length < 2 || cumLengths.length !== polyline.length) return null;
  let best: { station: number; offset: number } | null = null;
  const ab = new THREE.Vector3();
  const ap = new THREE.Vector3();
  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i];
    const b = polyline[i + 1];
    ab.subVectors(b, a);
    const segLen = ab.length();
    if (segLen < 1e-9) continue;
    ap.subVectors(point, a);
    const tRaw = ap.dot(ab) / (segLen * segLen);
    const t = Math.max(0, Math.min(1, tRaw));
    const closestX = a.x + ab.x * t;
    const closestY = a.y + ab.y * t;
    const closestZ = a.z + ab.z * t;
    const d = Math.hypot(point.x - closestX, point.y - closestY, point.z - closestZ);
    if (!best || d < best.offset) {
      best = { station: cumLengths[i] + segLen * t, offset: d };
    }
  }
  return best;
}

/** Sum of triangle areas in a non-indexed positions buffer (3 verts/tri). */
function trianglesArea(positions: number[]): number {
  let total = 0;
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const ab = new THREE.Vector3();
  const ac = new THREE.Vector3();
  for (let i = 0; i + 8 < positions.length; i += 9) {
    a.set(positions[i], positions[i + 1], positions[i + 2]);
    b.set(positions[i + 3], positions[i + 4], positions[i + 5]);
    c.set(positions[i + 6], positions[i + 7], positions[i + 8]);
    ab.subVectors(b, a);
    ac.subVectors(c, a);
    total += ab.cross(ac).length() * 0.5;
  }
  return total;
}

// Parse a space-separated coordinate string "northing easting elevation" into [easting, northing, elevation] (Z-up)
const parsePoint = (text: string): [number, number, number] => {
  const parts = text.trim().split(/\s+/).map(Number);
  return [parts[1], parts[0], parts[2] ?? 0];
};

// Interpolate points along a circular arc
const interpolateArc = (
  start: [number, number, number],
  end: [number, number, number],
  _radius: number,
  _dir: string,
  segments = 16,
): [number, number, number][] => {
  const points: [number, number, number][] = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    points.push([
      start[0] + (end[0] - start[0]) * t,
      start[1] + (end[1] - start[1]) * t,
      start[2] + (end[2] - start[2]) * t,
    ]);
  }
  return points;
};

// Parse <CoordGeom> element into a polyline of 3D points
const parseCoordGeom = (coordGeom: Element): [number, number, number][] => {
  const points: [number, number, number][] = [];

  for (const child of Array.from(coordGeom.children)) {
    if (child.tagName === 'Line') {
      const startEl = child.querySelector('Start');
      const endEl = child.querySelector('End');
      if (startEl?.textContent && endEl?.textContent) {
        if (points.length === 0) points.push(parsePoint(startEl.textContent));
        points.push(parsePoint(endEl.textContent));
      }
    } else if (child.tagName === 'Curve') {
      const startEl = child.querySelector('Start');
      const endEl = child.querySelector('End');
      const radius = parseFloat(child.getAttribute('radius') || '0');
      const dir = child.getAttribute('rot') || 'cw';
      if (startEl?.textContent && endEl?.textContent) {
        const start = parsePoint(startEl.textContent);
        const end = parsePoint(endEl.textContent);
        if (points.length === 0) points.push(start);
        const arcPoints = interpolateArc(start, end, radius, dir);
        points.push(...arcPoints.slice(1));
      }
    } else if (child.tagName === 'Spiral') {
      const startEl = child.querySelector('Start');
      const endEl = child.querySelector('End');
      if (startEl?.textContent && endEl?.textContent) {
        if (points.length === 0) points.push(parsePoint(startEl.textContent));
        points.push(parsePoint(endEl.textContent));
      }
    }
  }
  return points;
};

export const parseBreaklines = (doc: Document): LandXMLFeature[] => {
  const results: LandXMLFeature[] = [];
  const surfaces = doc.querySelectorAll('Surface');

  for (const surface of Array.from(surfaces)) {
    const surfaceName = surface.getAttribute('name') || 'Surface';
    const sourceData = surface.querySelector('SourceData');
    if (!sourceData) continue;

    const breaklinesContainer = sourceData.querySelector('Breaklines');
    if (!breaklinesContainer) continue;

    const breaklineElements = breaklinesContainer.querySelectorAll('Breakline');
    for (const breakline of Array.from(breaklineElements)) {
      const breaklineName = breakline.getAttribute('name') || 'Breakline';
      const pntList3D = breakline.querySelector('PntList3D');

      if (pntList3D?.textContent) {
        const coords = pntList3D.textContent.trim().split(/\s+/).map(Number);
        const points: THREE.Vector3[] = [];

        for (let i = 0; i < coords.length; i += 3) {
          const northing = coords[i];
          const easting = coords[i + 1];
          const elevation = coords[i + 2] ?? 0;
          points.push(new THREE.Vector3(easting, northing, elevation));
        }

        if (points.length >= 2) {
          const geometry = new THREE.BufferGeometry().setFromPoints(points);
          results.push({
            geometry,
            name: `${surfaceName} - ${breaklineName}`,
            category: 'BREAKLINE',
            metadata: { length: polylineLength(points) },
          });
        }
      }
    }
  }

  return results;
};

export const parseSurfaces = (doc: Document): LandXMLFeature[] => {
  const results: LandXMLFeature[] = [];
  const surfaces = doc.querySelectorAll('Surface');

  for (const surface of Array.from(surfaces)) {
    const name = surface.getAttribute('name') || 'Surface';
    const definition = surface.querySelector('Definition');
    if (!definition) continue;

    const pntsEl = definition.querySelector('Pnts');
    const facesEl = definition.querySelector('Faces');
    if (!pntsEl || !facesEl) continue;

    const pointMap = new Map<number, [number, number, number]>();
    const pElements = pntsEl.querySelectorAll('P');
    for (const p of Array.from(pElements)) {
      const id = parseInt(p.getAttribute('id') || '0');
      if (p.textContent) {
        pointMap.set(id, parsePoint(p.textContent));
      }
    }

    const fElements = facesEl.querySelectorAll('F');
    const positions: number[] = [];
    let zMin = Infinity;
    let zMax = -Infinity;
    let triangleCount = 0;
    for (const f of Array.from(fElements)) {
      if (!f.textContent) continue;
      const ids = f.textContent.trim().split(/\s+/).map(Number);
      if (ids.length < 3) continue;

      const p0 = pointMap.get(ids[0]);
      const p1 = pointMap.get(ids[1]);
      const p2 = pointMap.get(ids[2]);
      if (!p0 || !p1 || !p2) continue;

      positions.push(p0[0], p0[1], p0[2]);
      positions.push(p1[0], p1[1], p1[2]);
      positions.push(p2[0], p2[1], p2[2]);
      for (const z of [p0[2], p1[2], p2[2]]) {
        if (z < zMin) zMin = z;
        if (z > zMax) zMax = z;
      }
      triangleCount++;
    }

    if (positions.length === 0) continue;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.computeVertexNormals();

    // Flip normals to ensure they point upward (toward positive Z in our Z-up system)
    const normals = geometry.getAttribute('normal');
    if (normals) {
      for (let i = 0; i < normals.count; i++) {
        if (normals.getZ(i) < 0) {
          normals.setX(i, -normals.getX(i));
          normals.setY(i, -normals.getY(i));
          normals.setZ(i, -normals.getZ(i));
        }
      }
      normals.needsUpdate = true;
    }

    results.push({
      geometry,
      name,
      category: 'SURFACE',
      metadata: {
        surfaceArea: trianglesArea(positions),
        triangleCount,
        vertexCount: pointMap.size,
        zMin: Number.isFinite(zMin) ? zMin : undefined,
        zMax: Number.isFinite(zMax) ? zMax : undefined,
      },
    });
  }

  return results;
};

export const parseAlignments = (doc: Document): LandXMLFeature[] => {
  const results: LandXMLFeature[] = [];
  const alignments = doc.querySelectorAll('Alignments > Alignment');

  for (const alignment of Array.from(alignments)) {
    const name = alignment.getAttribute('name') || 'Alignment';
    const coordGeom = alignment.querySelector('CoordGeom');
    if (!coordGeom) continue;

    const tuples = parseCoordGeom(coordGeom);
    if (tuples.length < 2) continue;

    const polyline = tuples.map(([x, y, z]) => new THREE.Vector3(x, y, z));
    const geometry = new THREE.BufferGeometry().setFromPoints(polyline);
    const cumLengths = cumulativePolylineLengths(polyline);
    results.push({
      geometry,
      name,
      category: 'ALIGNMENT',
      metadata: {
        polyline,
        cumLengths,
        length: cumLengths[cumLengths.length - 1] ?? 0,
      },
    });
  }

  return results;
};

export const parseParcels = (doc: Document): LandXMLFeature[] => {
  const results: LandXMLFeature[] = [];
  const parcels = doc.querySelectorAll('Parcels > Parcel');

  for (const parcel of Array.from(parcels)) {
    const name = parcel.getAttribute('name') || 'Parcel';
    const coordGeom = parcel.querySelector('CoordGeom');
    if (!coordGeom) continue;

    const tuples = parseCoordGeom(coordGeom);
    if (tuples.length < 2) continue;

    // Close the boundary
    if (
      tuples.length > 2 &&
      (tuples[0][0] !== tuples[tuples.length - 1][0] ||
        tuples[0][1] !== tuples[tuples.length - 1][1] ||
        tuples[0][2] !== tuples[tuples.length - 1][2])
    ) {
      tuples.push(tuples[0]);
    }

    const polyline = tuples.map(([x, y, z]) => new THREE.Vector3(x, y, z));
    const geometry = new THREE.BufferGeometry().setFromPoints(polyline);
    results.push({
      geometry,
      name,
      category: 'PARCEL',
      metadata: {
        parcelArea: polygonAreaXY(polyline),
        length: polylineLength(polyline),
      },
    });
  }

  return results;
};

// --- Pipe Network types and helpers ---

interface ParsedStruct {
  name: string;
  center: [number, number, number];
  innerDiameter: number;
  outerDiameter: number;
  height: number;
  invertElevs: Map<string, number>;
}

export type PipeShape = 'circular' | 'egg' | 'elliptical' | 'rectangular';

const parsePipeShape = (pipeEl: Element): { diameter: number; shape: PipeShape } => {
  const circPipe = pipeEl.querySelector('CircPipe');
  if (circPipe) {
    return { diameter: parseFloat(circPipe.getAttribute('diameter') || '3000') / 1000, shape: 'circular' };
  }

  const eggPipe = pipeEl.querySelector('EggPipe');
  if (eggPipe) {
    const h = parseFloat(eggPipe.getAttribute('height') || '0.3');
    const w = parseFloat(eggPipe.getAttribute('width') || String(h * 0.67));
    return { diameter: Math.max(w, h), shape: 'egg' };
  }

  const elliPipe = pipeEl.querySelector('ElliPipe');
  if (elliPipe) {
    const h = parseFloat(elliPipe.getAttribute('height') || '0.3');
    const w = parseFloat(elliPipe.getAttribute('width') || '0.3');
    return { diameter: Math.max(w, h), shape: 'elliptical' };
  }

  const rectPipe = pipeEl.querySelector('RectPipe');
  if (rectPipe) {
    const h = parseFloat(rectPipe.getAttribute('height') || '0.3');
    const w = parseFloat(rectPipe.getAttribute('width') || '0.3');
    return { diameter: Math.sqrt(w * w + h * h), shape: 'rectangular' };
  }

  return { diameter: 0.3, shape: 'circular' };
};

const buildPipeCylinder = (start: THREE.Vector3, end: THREE.Vector3, radius: number): THREE.BufferGeometry => {
  const direction = new THREE.Vector3().subVectors(end, start);
  const length = direction.length();
  if (length < 0.001) return new THREE.BufferGeometry();

  const geometry = new THREE.CylinderGeometry(radius, radius, length, 8);
  const midpoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
  const quaternion = new THREE.Quaternion();
  quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.clone().normalize());

  geometry.applyQuaternion(quaternion);
  geometry.translate(midpoint.x, midpoint.y, midpoint.z);
  return geometry;
};

const buildStructGeometry = (struct: ParsedStruct): THREE.BufferGeometry => {
  const radius = (struct.outerDiameter || struct.innerDiameter || 1.0) / 2;
  const height = struct.height || 2.0;
  const [x, y, z] = struct.center;

  const geometry = new THREE.CylinderGeometry(radius, radius, height, 12);
  geometry.rotateX(Math.PI / 2);
  geometry.translate(x, y, z + height / 2);
  return geometry;
};

export const parsePipeNetworks = (doc: Document): PipeNetworkResult => {
  const meshes: LandXMLFeature[] = [];
  const lines: LandXMLFeature[] = [];
  const pipeNetworks = doc.querySelectorAll('PipeNetworks > PipeNetwork');

  for (const network of Array.from(pipeNetworks)) {
    const networkName = network.getAttribute('name') || 'PipeNetwork';

    // Parse structures
    const structMap = new Map<string, ParsedStruct>();
    const structs = network.querySelectorAll('Structs > Struct');
    for (const structEl of Array.from(structs)) {
      const structName = structEl.getAttribute('name') || '';
      const center = structEl.querySelector('Center');
      if (!center?.textContent) continue;

      const [x, y, z] = parsePoint(center.textContent);

      const invertElevs = new Map<string, number>();
      for (const inv of Array.from(structEl.querySelectorAll('InvertElev'))) {
        const elev = parseFloat(inv.getAttribute('elev') || '0');
        const refPipe = inv.getAttribute('refPipe') || '';
        if (refPipe) invertElevs.set(refPipe, elev);
      }

      const circStruct = structEl.querySelector('CircStruct');
      const rectStruct = structEl.querySelector('RectStruct');

      let innerDiameter = 1.0;
      let outerDiameter = 1.2;
      let height = 2.0;

      if (circStruct) {
        innerDiameter = parseFloat(circStruct.getAttribute('diameter') || '1000.0') / 1000;
        outerDiameter = innerDiameter + 0.2;
      } else if (rectStruct) {
        const w = parseFloat(rectStruct.getAttribute('width') || '1.0');
        const l = parseFloat(rectStruct.getAttribute('length') || '1.0');
        innerDiameter = Math.max(w, l);
        outerDiameter = innerDiameter + 0.2;
      }

      const elevSump = parseFloat(structEl.getAttribute('elevSump') || String(z));
      const elevRim = parseFloat(structEl.getAttribute('elevRim') || String(z + 2));
      height = Math.abs(elevRim - elevSump);
      if (height < 0.1) height = 2.0;

      const parsed: ParsedStruct = {
        name: structName,
        center: [x, y, elevSump],
        innerDiameter,
        outerDiameter,
        height,
        invertElevs,
      };
      structMap.set(structName, parsed);

      const structGeo = buildStructGeometry(parsed);
      if (structGeo.getAttribute('position')?.count) {
        meshes.push({
          geometry: structGeo,
          name: `${networkName} - ${structName}`,
          category: 'STRUCTURE',
          metadata: {
            rimElev: elevRim,
            sumpElev: elevSump,
            innerDiameter,
          },
        });
      }
    }

    // Parse pipes
    for (const pipeEl of Array.from(network.querySelectorAll('Pipes > Pipe'))) {
      const pipeName = pipeEl.getAttribute('name') || 'Pipe';
      const startStruct = structMap.get(pipeEl.getAttribute('refStart') || '');
      const endStruct = structMap.get(pipeEl.getAttribute('refEnd') || '');
      if (!startStruct || !endStruct) continue;

      const { diameter, shape } = parsePipeShape(pipeEl);
      const pipeRadius = diameter / 2;

      const invertStart = startStruct.invertElevs.get(pipeName) ?? startStruct.center[2];
      const invertEnd = endStruct.invertElevs.get(pipeName) ?? endStruct.center[2];
      const startZ = invertStart + pipeRadius;
      const endZ = invertEnd + pipeRadius;

      const startPt = new THREE.Vector3(startStruct.center[0], startStruct.center[1], startZ);
      const endPt = new THREE.Vector3(endStruct.center[0], endStruct.center[1], endZ);
      const length3D = startPt.distanceTo(endPt);
      const horizLength = Math.hypot(
        endStruct.center[0] - startStruct.center[0],
        endStruct.center[1] - startStruct.center[1],
      );
      const slope = horizLength > 0 ? (invertEnd - invertStart) / horizLength : 0;

      const metadata: LandXMLFeatureMetadata = {
        diameter,
        pipeShape: shape,
        invertStart,
        invertEnd,
        slope,
        length: length3D,
      };

      const pipeGeo = buildPipeCylinder(startPt, endPt, pipeRadius);
      if (pipeGeo.getAttribute('position')?.count) {
        meshes.push({
          geometry: pipeGeo,
          name: `${networkName} - ${pipeName}`,
          category: 'PIPE',
          metadata,
        });
      }

      const lineGeo = new THREE.BufferGeometry().setFromPoints([startPt, endPt]);
      lines.push({
        geometry: lineGeo,
        name: `${networkName} - ${pipeName} (centerline)`,
        category: 'PIPE',
        metadata,
      });
    }
  }

  return { meshes, lines };
};
